import {
  BKJButton,
  BKJCheckbox,
  BKJIconTooltip,
  BKJLoadingSpinner,
  BKJSelectSearch,
  BKJTextInput,
  Flex,
} from '@bookjane2/bookjane-design-library';
import { H3 } from 'components/Typography';
import { useTranslation } from 'hooks/useTranslation';
import {
  PositionsAllocationSettingsPageDataProvider,
  usePositionsAllocationSettingsPageDataContext,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
import {
  PositionsAllocationSettingsPageFormProvider,
  usePositionsAllocationSettingsPageFormContext,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocationSettingsPageForm.form.context';
import { ChangeEvent, FC, Fragment, useState, useCallback, useMemo } from 'react';
import { Route, Switch, useParams, useRouteMatch } from 'react-router-dom';
import { ReadView } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocation.read.component';

import { css } from 'styled-components';
import { LoadingWrapper } from 'pages/Settings/pages/ADP/ADPAuthRedirectPage/ADPIntegration/AfterADPIntegration/AfterADPIntegration.styled';
import {
  PositionsAllocationForm,
  UnderlineButton,
  CheckboxText,
  PositionsAllocationGroup,
  PositionsAllocationTableContainer,
  PositionsAllocationTableWrapper,
  PositionsAllocationNote,
  PositionsAllocationTableRowWrapper,
  PositionsAllocationContentWrapper,
  PositionsAllocationSubHeaderTableData,
  PositionsAllocationSubHeaderWrapper,
  PositionsAllocationSubHeaderTitle,
  PositionsAllocationTableBody,
  EmptyStateMessage,
  ChargeRateTableData,
  ChargeRateField,
  ChargeRateWarningIcon,
  ChargeRateWarningTooltip,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocation.styled';
import { RemovePositionDialog } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionDialog';
import { toastService } from 'services/toastService';
import {
  FetchAssignedGroupedPositionsProvider,
  useFetchAssignedGroupedPositionsContext,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/FetchAssignedGroupedPositions.data.context';
import { removePositionModalRoutingService } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { useLocationRecordDetailDataContext } from 'pages/Settings/pages/LocationSettings/LocationRecordDetail.data.context';
import { PositionsAllocationSettingsPageFormValues } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocationSettingsPageForm.types';

const PositionsAllocationTable: FC = () => {
  const { data: assignedPositions, refetch: fetchAssignedPositions } =
    usePositionsAllocationSettingsPageDataContext();
  const hasMarketplace = !!assignedPositions?.fetchCommunity?.hasMarketplace;
  const { values, chargeRateErrors } = usePositionsAllocationSettingsPageFormContext();
  const { onChange, onReset } = usePositionsAllocationSettingsPageFormContext();

  const { locationId } = useParams<{ locationId: string }>();
  const [isRemovePositionConfirmationOpen, setIsRemovePositionConfirmationOpen] = useState(false);
  const [removePositionById, setRemovePositionById] = useState<number>();
  const handleClickRemovePosition = useCallback(
    (positionId: number, isShowRemovePositionFlow: boolean) => {
      if (isShowRemovePositionFlow) {
        return removePositionModalRoutingService.open({ positionId }, 'RemovePositionView');
      }
      setRemovePositionById(positionId);
      return setIsRemovePositionConfirmationOpen(true);
    },
    [],
  );
  const { t } = useTranslation();
  const handleChargeRateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const decimalPart = event.target.value.split('.')[1];
      if (decimalPart && decimalPart.length > 2) return;
      onChange(event);
    },
    [onChange],
  );
  const { refetch, data } = useFetchAssignedGroupedPositionsContext();
  const { data: LocationData } = useLocationRecordDetailDataContext();
  const searchPositionsEmptyStateMessage = useMemo(
    () =>
      data?.groupPositions?.edges.length === 0 && (
        <Flex
          justifyContent="space-between"
          margin="8px auto 0 auto"
          flexDirection="column"
          alignContent="center"
          gap="12px"
        >
          <EmptyStateMessage>
            {t('location_settings:NO_SEARCHED_POSITION_RESULTS')}
          </EmptyStateMessage>
        </Flex>
      ),
    [data, t],
  );
  return (
    <PositionsAllocationTableContainer>
      {removePositionById && (
        <RemovePositionDialog
          positionId={removePositionById}
          communityId={locationId}
          communityName={LocationData?.name}
          isRemovePositionConfirmationOpen={isRemovePositionConfirmationOpen}
          setIsRemovePositionConfirmationOpen={setIsRemovePositionConfirmationOpen}
          onSuccess={() => {
            toastService.success(t('success:REMOVE_POSITION'));
            onChange({
              target: { name: `positionLocation_${removePositionById}`, value: false },
            });
            refetch && refetch();
            fetchAssignedPositions && fetchAssignedPositions();
            onReset({} as PositionsAllocationSettingsPageFormValues);
          }}
        />
      )}
      {data?.groupPositions?.edges.map((group, index) => {
        const departmentName = group?.node?.group;
        return (
          <PositionsAllocationGroup key={index}>
            <PositionsAllocationTableBody
              gridTemplateColumns={hasMarketplace ? '40% 15% 15% 15% 15%' : '40% 25% 20% 15%'}
            >
              <PositionsAllocationSubHeaderWrapper>
                <PositionsAllocationSubHeaderTitle largeFont>
                  {departmentName}
                </PositionsAllocationSubHeaderTitle>
              </PositionsAllocationSubHeaderWrapper>
              {hasMarketplace && (
                <PositionsAllocationSubHeaderWrapper center>
                  <PositionsAllocationSubHeaderTitle>
                    {'Charge Rate'}
                  </PositionsAllocationSubHeaderTitle>
                </PositionsAllocationSubHeaderWrapper>
              )}
              <PositionsAllocationSubHeaderWrapper center>
                <PositionsAllocationSubHeaderTitle>
                  {t('location_settings:APPLY_TO_LOCATION')}
                </PositionsAllocationSubHeaderTitle>
              </PositionsAllocationSubHeaderWrapper>
              <PositionsAllocationSubHeaderWrapper center>
                <PositionsAllocationSubHeaderTitle>
                  {'Supervisor Role'}
                </PositionsAllocationSubHeaderTitle>
              </PositionsAllocationSubHeaderWrapper>
              <PositionsAllocationSubHeaderWrapper center>
                <PositionsAllocationSubHeaderTitle>
                  {t('location_settings:ACTION')}
                </PositionsAllocationSubHeaderTitle>
              </PositionsAllocationSubHeaderWrapper>
            </PositionsAllocationTableBody>

            <PositionsAllocationTableWrapper>
              <PositionsAllocationTableBody
                gridTemplateColumns={hasMarketplace ? '40% 15% 15% 15% 15%' : '40% 25% 20% 15%'}
              >
                {group?.node?.items.map((position, idx: number) => {
                  const isAssignedPosition =
                    assignedPositions?.fetchCommunity?.locationPositions.some(
                      (locPosition) => locPosition.id === position.id,
                    );
                  const chargeRate = position.chargeRate;
                  const chargeRateError = chargeRateErrors[`${position.id}`];
                  const isShowRemovePositionFlow =
                    assignedPositions?.fetchCommunity?.locationPositions.some(
                      (locPosition) =>
                        locPosition.id === position.id &&
                        (locPosition.configuredShifts.totalCount > 0 ||
                          locPosition.teamMembers?.totalCount > 0),
                    );
                  const isSupervised = position.supervisor;
                  const disabledCheckbox =
                    !isAssignedPosition && !values[`positionLocation_${position.id}`];

                  const otherSupervisorSelected = group?.node?.items.some(
                    (otherPosition) =>
                      otherPosition.id !== position.id && values[`supervisor_${otherPosition.id}`],
                  );

                  const supervisorCheckboxDisabled =
                    disabledCheckbox ||
                    (otherSupervisorSelected && !values[`supervisor_${position.id}`]);

                  return (
                    <PositionsAllocationTableRowWrapper key={idx} isGrey={idx % 2 === 0}>
                      <PositionsAllocationSubHeaderTableData>
                        {position.name}
                      </PositionsAllocationSubHeaderTableData>
                      {hasMarketplace && (
                        <ChargeRateTableData center>
                          <ChargeRateField hasError={!!chargeRateError}>
                            <BKJTextInput
                              type="number"
                              value={
                                values[`chargeRate_${position.id}`] ??
                                (chargeRate !== undefined && chargeRate !== null
                                  ? String(chargeRate)
                                  : '')
                              }
                              placeholder="Rate"
                              disabled={disabledCheckbox}
                              name={`chargeRate_${position.id}`}
                              onChange={handleChargeRateChange}
                            />
                            {chargeRateError && (
                              <ChargeRateWarningIcon>
                                <BKJIconTooltip iconName="Warning" placement="top">
                                  <ChargeRateWarningTooltip>
                                    {chargeRateError.join(' ')}
                                  </ChargeRateWarningTooltip>
                                </BKJIconTooltip>
                              </ChargeRateWarningIcon>
                            )}
                          </ChargeRateField>
                        </ChargeRateTableData>
                      )}
                      <PositionsAllocationSubHeaderTableData center>
                        <BKJCheckbox
                          variant="GreenSolid"
                          name={`positionLocation_${position.id}`}
                          onChange={(e) => onChange(e)}
                          value={isAssignedPosition}
                          disabled={isAssignedPosition}
                        />
                        <CheckboxText>
                          {isAssignedPosition && t('location_settings:APPLIED')}
                        </CheckboxText>
                      </PositionsAllocationSubHeaderTableData>
                      <PositionsAllocationSubHeaderTableData center>
                        <BKJCheckbox
                          variant="GreenSolid"
                          name={`supervisor_${position.id}`}
                          onChange={(e) => onChange(e)}
                          value={isSupervised}
                          disabled={supervisorCheckboxDisabled}
                        />
                      </PositionsAllocationSubHeaderTableData>
                      <PositionsAllocationSubHeaderTableData center>
                        <UnderlineButton
                          onClick={(e) => {
                            e.preventDefault();
                            handleClickRemovePosition(position.displayId, isShowRemovePositionFlow);
                          }}
                          variant="NoStyles"
                          disabled={!isAssignedPosition}
                        >
                          {t('settings:REMOVE')}
                        </UnderlineButton>
                      </PositionsAllocationSubHeaderTableData>
                    </PositionsAllocationTableRowWrapper>
                  );
                })}
              </PositionsAllocationTableBody>
            </PositionsAllocationTableWrapper>
          </PositionsAllocationGroup>
        );
      })}
      {searchPositionsEmptyStateMessage}
    </PositionsAllocationTableContainer>
  );
};

const EditViewComponent: FC = () => {
  const { isLoading: isAssignedPositionDataLoading } =
    usePositionsAllocationSettingsPageDataContext();
  const { onSubmit, isSubmitting, isDisabled } = usePositionsAllocationSettingsPageFormContext();

  const { t } = useTranslation();
  const {
    onChange: onChangeSearchFilter,
    values: fetchPositionsFilterValues,
    isLoading,
  } = useFetchAssignedGroupedPositionsContext();

  return (
    <PositionsAllocationContentWrapper>
      <PositionsAllocationForm onSubmit={onSubmit} width="100%" maxHeight="600px">
        <Flex gap="40px" flexDirection="column">
          <H3>{t('location_settings:LOCATION_POSITIONS')}</H3>
          <PositionsAllocationNote>
            {t('location_settings:LOCATION_POSITIONS_PAGE_NOTE')}
          </PositionsAllocationNote>
        </Flex>
        <Flex flexDirection="column" flex="1" margin="32px 0 32px 0" maxWidth="522px">
          <BKJSelectSearch
            onChange={onChangeSearchFilter}
            name="byName"
            value={fetchPositionsFilterValues.byName}
            placeholder={t('settings:SEARCH_POSITIONS')}
          />
        </Flex>
        {isLoading || isAssignedPositionDataLoading ? (
          <LoadingWrapper>
            <BKJLoadingSpinner variant="Default" />
          </LoadingWrapper>
        ) : (
          <Fragment>
            <PositionsAllocationTable />
            <BKJButton
              css={css`
                font-size: 16px;
                min-height: 44px;
              `}
              variant="PurpleSolid"
              width="192px"
              minWidth="192px"
              type="submit"
              isLoading={isSubmitting}
              disabled={isDisabled}
            >
              {t('auth:SAVE')}
            </BKJButton>
          </Fragment>
        )}
      </PositionsAllocationForm>
    </PositionsAllocationContentWrapper>
  );
};

const EditView: FC = () => {
  return (
    <PositionsAllocationSettingsPageFormProvider>
      <EditViewComponent />
    </PositionsAllocationSettingsPageFormProvider>
  );
};

const Component: FC = () => {
  const match = useRouteMatch();
  return (
    <Switch>
      <Route exact path={match.path} component={ReadView} />
      <Route path={`${match.path}/edit`} component={EditView} />
    </Switch>
  );
};

export const PositionsAllocationSettingsPageForm = () => {
  return (
    <PositionsAllocationSettingsPageDataProvider>
      <FetchAssignedGroupedPositionsProvider>
        <Component />
      </FetchAssignedGroupedPositionsProvider>
    </PositionsAllocationSettingsPageDataProvider>
  );
};
